import { useEffect, useState } from 'react'
import config from "../config"
import { regionTheme, REGION_INK, REGION_SIL_STROKE } from '../constants/regionTheme'

interface LoginProps {
  onLoginSuccess: (username: string) => void
  notice?: string
  onConsumeNotice?: () => void
}

interface LoginForm {
  username: string
  password: string
}

export default function Login({ onLoginSuccess, notice: noticeProp, onConsumeNotice }: LoginProps) {
  const [loginForm, setLoginForm] = useState<LoginForm>({ username: '', password: '' })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [notice, setNotice] = useState<string>('')
  const [region, setRegion] = useState<string>('')

  const normalizeLoginErrorMessage = (detail: unknown): string => {
    const msg = typeof detail === 'string' ? detail : ''
    if (!msg) return '로그인에 실패했습니다.'

    // 백엔드 구버전/프록시 등에서 내려오는 레거시 문구도 사용자 친화적으로 변환
    if (msg === 'Inactive user') {
      return '계정이 비활성화되어 로그인할 수 없습니다. 관리자에게 계정 활성화를 요청해 주세요.'
    }

    return msg
  }

  // 어느 리전에 들어왔는지 — 로그인 전이라 /auth/me 대신 공개 조회를 쓴다. 실패하면 표시만 안 한다.
  useEffect(() => {
    fetch(`${config.API_BASE_URL}/auth/region`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (typeof d?.region === 'string') setRegion(d.region) })
      .catch(() => {})
  }, [])

  // 세션 만료 안내 배너
  useEffect(() => {
    try {
      const expired = localStorage.getItem('session_expired')
      if (expired === '1') {
        setNotice('세션이 만료되었습니다. 다시 로그인해주세요.')
        localStorage.removeItem('session_expired')
      }
    } catch {}
  }, [])

  // 상위(App)에서 전달된 안내 공지 처리 (레이스 방지)
  useEffect(() => {
    if (noticeProp && noticeProp.trim().length > 0) {
      setNotice(noticeProp)
      try { onConsumeNotice && onConsumeNotice() } catch {}
    }
  }, [noticeProp])

  const handleLogin = async (e?: React.FormEvent): Promise<void> => {
    if (e) {
      e.preventDefault()
    }
    
    setIsLoading(true)
    setError('')

    try {
      // 실제 백엔드 API 호출
      const response = await fetch(`${config.API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        
        // JWT 토큰 저장
        localStorage.setItem('access_token', data.access_token)
        localStorage.setItem('refresh_token', data.refresh_token)
        
        // 로그인 성공 후 콜백 호출 (이메일과 함께)
        onLoginSuccess(loginForm.username)
      } else {
        let errorData: any = null
        try {
          errorData = await response.json()
        } catch {
          errorData = null
        }
        setError(normalizeLoginErrorMessage(errorData?.detail))
      }
    } catch (err) {
      console.error('Login error:', err)
      setError('서버 연결에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  // 리전 표시 (kr·us) — 배경 전체를 리전 색으로, 실루엣과 코드 글자를 크게 깐다. 카드는 그대로.
  const theme = regionTheme(region)

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: theme ? theme.bg : '#f5f5f5',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {theme && (
        <>
          <svg viewBox="96 8 126 134" preserveAspectRatio="xMidYMid meet" aria-hidden="true"
               style={{ position: 'absolute', right: '-60px', bottom: '-40px', width: '900px', height: '900px', opacity: 0.35 }}>
            <path d={theme.path} fill={theme.silFill} stroke={REGION_SIL_STROKE} strokeWidth="0.3" />
          </svg>
          <span aria-hidden="true" style={{
            position: 'absolute', left: '56px', bottom: '40px',
            fontSize: '160px', fontWeight: 800, letterSpacing: '-6px', lineHeight: 1,
            color: REGION_INK, opacity: 0.9,
          }}>
            {theme.code}
          </span>
        </>
      )}
      <div style={{
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        border: '1px solid #ddd',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '400px',
        maxWidth: '90%'
      }}>
        {/* 헤더 */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <img src="/logo.svg" alt="logo" />
              {theme && (
                <span style={{
                  fontSize: '14px',
                  color: '#9ca3af',
                  fontWeight: '500',
                  textAlign: 'end',
                  paddingRight: '5px',
                  fontStyle: 'oblique',
                }}>
                  MLOps Platform
                </span>
              )}
            </div>
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#333', margin: '0 0 8px 0' }}>로그인</h1>
          <p style={{ fontSize: '14px', color: '#666', margin: '0' }}>농업 AI 모델 개발 플랫폼에 로그인하세요</p>
        </div>

        <form onSubmit={handleLogin}>
          {/* 로그인 입력 필드들 */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="username" style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333',
                marginBottom: '8px'
              }}>
                아이디
              </label>
              <input
                id="username"
                type="text"
                placeholder="아이디를 입력하세요"
                value={loginForm.username}
                onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="password" style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#333', 
                marginBottom: '8px' 
              }}>
                비밀번호
              </label>
              <input
                id="password"
                type="password"
                placeholder="비밀번호를 입력하세요"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}
          </div>
          
        {notice && (
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            color: '#92400e',
            padding: '10px 12px',
            borderRadius: '8px',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            {notice}
          </div>
        )}

          {/* 실제 API 로그인 버튼 */}
          <button 
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              backgroundColor: '#000',
              color: 'white',
              padding: '12px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '10px'
            }}
          >
            {isLoading ? '로그인 중...' : '로그인'}
          </button>
        </form>
      </div>
    </div>
  )
}
